/**
 * <Select> — canonical native-select primitive.
 *
 * Same Field-context behavior as <Input>: when wrapped in <Field>, the
 * select automatically picks up the generated id, aria-describedby,
 * aria-invalid, aria-required, and disabled state. Falls back to whatever
 * the caller passed when used standalone.
 *
 * Sizes match Input: sm (32px) / md (36px / default) / lg (40px).
 *
 * Spec: docs/design-system/06-buttons-forms.md.
 */

import { forwardRef, type SelectHTMLAttributes } from 'react';
import { ChevronDown } from 'lucide-react';
import { cn } from '../../utils/cn';
import { useFieldContext, describedByFromContext } from '../Field/FieldContext';

export type SelectSize = 'sm' | 'md' | 'lg';

export interface SelectProps extends Omit<SelectHTMLAttributes<HTMLSelectElement>, 'size'> {
  size?: SelectSize;
  /** Show as full width of parent. Default `true` (forms expect this). */
  fullWidth?: boolean;
  /** Force invalid state when used standalone. Inside a <Field>, error wins. */
  error?: boolean;
}

const SIZE_CLASSES: Record<SelectSize, string> = {
  sm: 'h-8 pl-3 pr-9 text-sm',
  md: 'h-9 pl-3 pr-9 text-sm',
  lg: 'h-10 pl-4 pr-10 text-md',
};

export const Select = forwardRef<HTMLSelectElement, SelectProps>(function Select(
  {
    size = 'md',
    fullWidth = true,
    error: errorProp,
    className,
    id: idProp,
    'aria-describedby': describedByProp,
    'aria-invalid': invalidProp,
    'aria-required': requiredProp,
    'aria-disabled': _ariaDisabledProp,
    disabled: disabledProp,
    required: requiredAttr,
    children,
    ...rest
  },
  ref,
) {
  const ctx = useFieldContext();

  const id = ctx?.inputId ?? idProp;
  const describedBy = describedByFromContext(ctx, describedByProp);
  const invalid = ctx?.invalid ?? errorProp ?? false;
  const required = ctx?.required ?? requiredAttr ?? false;
  // Most-restrictive-wins: Field always forwards a `disabled` value into
  // context (default `false` when the caller never set one on <Field>
  // itself), so `??` alone would let that default short-circuit a truthy
  // `disabled` passed directly to this control. OR-ing the two means either
  // side setting `disabled` disables the rendered control, matching the
  // "most restrictive wins" contract instead of silently favoring context.
  const disabled = Boolean(ctx?.disabled) || Boolean(disabledProp);

  return (
    <div className={cn('relative', fullWidth && 'w-full')}>
      <select
        ref={ref}
        id={id}
        aria-describedby={describedBy}
        aria-invalid={invalid || undefined}
        aria-required={required || undefined}
        // Redundant with the native `disabled` attribute below (which already
        // implies this to assistive tech) — kept explicit so AT and any
        // `[aria-disabled]` styling hook see the state directly. Intentional.
        aria-disabled={disabled || undefined}
        disabled={disabled}
        required={required}
        className={cn(
          SIZE_CLASSES[size],
          // Native select arrow gets hidden; we render our own ChevronDown.
          'appearance-none bg-surface border rounded-md text-text',
          'focus:outline-none focus:ring-2 transition-colors cursor-pointer',
          'disabled:bg-bg disabled:opacity-60 disabled:cursor-not-allowed',
          invalid
            ? 'border-danger focus:ring-danger/20 focus:border-danger'
            : 'border-border focus:ring-primary/20 focus:border-primary',
          fullWidth && 'w-full',
          // Native option text needs explicit dark-mode colors.
          '[&>option]:bg-elevated [&>option]:text-text',
          className,
        )}
        {...rest}
      >
        {children}
      </select>
      <ChevronDown
        className={cn(
          'absolute right-3 top-1/2 -translate-y-1/2 text-muted pointer-events-none shrink-0',
          size === 'lg' ? 'w-5 h-5' : 'w-4 h-4',
        )}
        aria-hidden="true"
      />
    </div>
  );
});

Select.displayName = 'Select';

export default Select;
